import React, { memo, useEffect, useState } from 'react';
import { Bar, BarChart, CartesianGrid, Tooltip, XAxis, YAxis } from 'recharts';
import styled, { ThemeProps } from 'styled-components';
import { P4, P2 } from 'styleguide/Texts';
import { View } from 'styleguide/View';
import { colors } from 'styleguide/colors';
import fonts from 'styleguide/fonts';
import { CornerRadius, Spacing } from 'styleguide/spacing';
import { ClipLoader } from 'react-spinners';
import { themeState } from 'styleguide/theme';
import { Theme } from 'styleguide/types';
import { ResponsiveGraphContainer } from 'components/ResponsiveGraphContainer';
import dayjs from 'dayjs';
import { ProfileBarGraphUnit as ProfileBarGraphUnit } from '../ProfileViewModel';
import { secondsToWordFormatMinutesLocalized } from 'translations/units';
import { formatDateTime, formatNumberWithUnit } from 'translations/formatters';
import { PROFILE_BAR_CHART_INTERNAL_DATE_FORMAT } from '../utils';
import { TimeFrameValue } from './TimeFrameSelector';

const BAR_CHART_HEIGHT = 180;
const CHART_TOOLTIP_WIDTH = 120;
const X_AXIS_HEIGHT = 30;

const Container = styled(View)`
  flex: 1;
`;

// Custom bar shape with rounded top corners
const CustomBarShape = (props: {
  fill?: string;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
}): React.ReactElement => {
  const { fill, x = 0, y = 0, width = 0, height = 0 } = props;

  // Adaptive radius: smaller for very short bars to prevent weird appearance
  // For bars shorter than 8px, use no rounding. For bars 8-16px, use proportional rounding.
  // For taller bars, use full 4px radius.
  const maxRadius = 4;
  const minHeightForRounding = 8;
  let radius = 0;

  if (height >= minHeightForRounding) {
    if (height < 16) {
      // Proportional radius for medium-height bars
      radius = (height / 16) * maxRadius;
    } else {
      // Full radius for tall bars
      radius = maxRadius;
    }
  }

  // If height is 0 or negative, return a minimal invisible path
  if (height <= 0) {
    return <path d={`M ${x},${y} Z`} fill={fill} opacity={0} />;
  }

  // If radius is 0 or very small, use a simple rectangle
  if (radius < 0.5) {
    return <rect x={x} y={y} width={width} height={height} fill={fill} />;
  }

  // Ensure radius doesn't exceed half the width or height
  const clampedRadius = Math.min(radius, width / 2, height / 2);

  // Create a path for a rectangle with rounded top corners
  const path = `
    M ${x},${y + height}
    L ${x},${y + clampedRadius}
    Q ${x},${y} ${x + clampedRadius},${y}
    L ${x + width - clampedRadius},${y}
    Q ${x + width},${y} ${x + width},${y + clampedRadius}
    L ${x + width},${y + height}
    Z
  `;

  return <path d={path} fill={fill} />;
};

interface BarGraphDataPoint {
  date: string;
  value: number;
}

export type BarGraphData = BarGraphDataPoint[];

interface ProfileBarChartProps {
  data: BarGraphData;
  unit: ProfileBarGraphUnit;
  isLoading?: boolean;
  timeFrame: TimeFrameValue;
}

export const ProfileBarChart = memo(
  ({ data, unit, isLoading, timeFrame }: ProfileBarChartProps) => {
    const [valuePositionData, setposData] = useState<{ x: number; y: number }>({ x: 0, y: 0 });

    // We use these min/max state values so we can calculate the tooltip position
    const [dataMaxValue, setDataMax] = useState(0);
    const [dataMinValue, setDataMin] = useState(0);

    const durationYAxisTicks = Array.from(
      { length: Math.ceil(dataMaxValue / 3600) + 1 },
      (_, index) => index * 3600,
    );

    // We keep track of whether the mouse is in the container so we can
    // show the tooltip till the mouse is completely outside the chart area
    const [isMouseInsideChartContainer, setIsMouseInsideChartContainer] = useState(false);
    return (
      <Container
        onMouseLeave={() => setIsMouseInsideChartContainer(false)}
        onMouseEnter={() => setIsMouseInsideChartContainer(true)}
      >
        {isLoading ? (
          <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
            <ClipLoader size={20} color={colors.primary} loading={true} />
          </View>
        ) : (
          <ResponsiveGraphContainer>
            <BarChart
              data={data}
              style={{ height: BAR_CHART_HEIGHT }}
              barSize={Spacing.lg}
              // Helps ensure selected dots don't clip
              margin={{ top: Spacing.lg, right: Spacing.lg }}
              onMouseMove={data => {
                if (data.activeCoordinate && data.activePayload?.[0]?.payload.value !== null) {
                  const activePayloadValue = data.activePayload?.[0]?.payload.value;

                  let percentOfChartHeight = 0;
                  if (dataMaxValue === dataMinValue) {
                    // There is only one value
                    percentOfChartHeight = 0.5;
                  } else {
                    // Okay, for some reason recharts doesn't give us the "y" value of the
                    // currently active POINT (it gives us the mouse location), so
                    // let's do some math to find where the point is:
                    percentOfChartHeight =
                      (activePayloadValue - dataMinValue) / (dataMaxValue - dataMinValue);
                  }

                  // -40 here because there is some padding around the chart. I'm not exactly
                  // sure what the amount is, but this seems to position the tooltip
                  // consistently vertically
                  let y = -((BAR_CHART_HEIGHT - X_AXIS_HEIGHT) * percentOfChartHeight);

                  // +80 puts the tooltip in the right absolute position compared
                  // with the top of the bar
                  y = y + 80;
                  setposData({ x: data.activeCoordinate.x - CHART_TOOLTIP_WIDTH / 2, y: y });
                }
              }}
            >
              <CartesianGrid
                vertical={false}
                stroke={themeState.current.name === 'light' ? colors.greyLight : colors.greyDark}
                strokeDasharray="3 3"
              />
              <XAxis
                // Required to keep the text values from clipping
                height={X_AXIS_HEIGHT}
                dataKey="date"
                tickLine={false}
                axisLine={{
                  strokeWidth: 1,
                  stroke: themeState.current.name === 'light' ? colors.greyLight : colors.greyDark,
                }}
                minTickGap={0}
                tickMargin={Spacing.md}
                color={colors.grey}
                tick={
                  <CustomizedXAxisLabel x={0} y={0} payload={undefined} timeFrame={timeFrame} />
                }
              />
              <YAxis
                scale={'linear'}
                domain={[
                  () => {
                    setDataMin(0); // We need this to calculate tooltip position
                    return 0;
                  },
                  (dataMax: number) => {
                    setDataMax(dataMax); // We need this to calculate tooltip position
                    return dataMax;
                  },
                ]}
                axisLine={false}
                tickLine={false}
                tickMargin={Spacing.md}
                allowDecimals={false}
                tick={<CustomizedYAxisLabel x={0} y={0} payload={undefined} unit={unit} />}
                ticks={unit === 'hr' ? durationYAxisTicks : undefined}
              />
              <Tooltip
                cursor={false}
                trigger="hover"
                position={{ x: valuePositionData?.x ?? 0, y: valuePositionData?.y ?? 0 }}
                allowEscapeViewBox={{ x: true, y: true }}
                // This keeps the OS from drawing a "dialog selected" box around the tooltip
                wrapperStyle={{ outline: '0px', visibility: 'visible' }}
                content={
                  // Actual props (except unit and isMouseInsideContainer) will be passed by Tooltip under the hood
                  <ChartTooltip
                    isMouseInsideContainer={isMouseInsideChartContainer}
                    unit={unit}
                    payload={undefined}
                    active={false}
                    label=""
                    isWeekView={timeFrame === 'last-12-weeks'}
                  />
                }
              />
              <Bar
                dataKey="value"
                fill={colors.primary}
                isAnimationActive={true}
                shape={CustomBarShape}
              />
            </BarChart>
          </ResponsiveGraphContainer>
        )}
      </Container>
    );
  },
);

const DownTriangle = styled.div`
  width: 0;
  height: 0;
  border-left: 10px solid transparent;
  border-right: 10px solid transparent;
  border-top: 10px solid
    ${(props: ThemeProps<Theme>) =>
      props.theme.name === 'light' ? colors.greyUltraLight : colors.greyUltraDark};
  align-self: center;
`;

const ChartTooltip = ({
  isMouseInsideContainer,
  payload,
  label,
  unit,
  isWeekView,
}: {
  isMouseInsideContainer: boolean;
  active: boolean;
  payload: any;
  label: string;
  unit: string;
  isWeekView?: boolean;
}) => {
  // Okay, so Recharts clears the data when the mouse goes outside the chart, but we want
  // to keep rendering the tooltip till the mouse goes outside the CONTAINER. We keep track
  // of the last value here so we can render until isMouseInsideContainer goes false.
  const [lastPayload, setLastPayload] = useState<any>({});
  useEffect(() => {
    if (payload && payload.length > 0) {
      setLastPayload(payload);
    }
  }, [payload]);
  if (lastPayload && lastPayload.length && lastPayload[0].value !== 0 && isMouseInsideContainer) {
    const date = dayjs(label, 'YYYY-MM-DD');
    const startDateLabel = formatDateTime(date.toDate(), { month: 'short', day: 'numeric' });
    const endDateLabel = formatDateTime(date.add(1, 'week').toDate(), {
      month: 'short',
      day: 'numeric',
    });

    return (
      <View onClick={event => event.stopPropagation()}>
        <View
          style={{
            backgroundColor:
              themeState.current.name === 'light' ? colors.greyUltraLight : colors.greyUltraDark,
            borderRadius: CornerRadius.md,
            minWidth: CHART_TOOLTIP_WIDTH,
          }}
        >
          <View style={{ padding: Spacing.md, paddingBottom: Spacing.xs }}>
            <P4>{!!isWeekView ? `${startDateLabel} - ${endDateLabel}` : startDateLabel}</P4>
            <P2 type="secondary">
              {unit === 'hr'
                ? secondsToWordFormatMinutesLocalized(lastPayload[0].value as number)
                : formatNumberWithUnit(lastPayload[0].value as number, 'reps', {
                    maximumFractionDigits: 0,
                  })}
            </P2>
          </View>
        </View>
        <DownTriangle />
      </View>
    );
  }

  return null;
};

type CustomizedYAxisLabelProps = {
  x: number;
  y: number;
  payload: any;
  unit: ProfileBarGraphUnit;
};

const CustomizedYAxisLabel = (props: CustomizedYAxisLabelProps) => {
  const { x, y, payload, unit } = props;

  const formatYValue = (value: number) => {
    switch (unit) {
      case 'hr':
        const hours = Math.floor(value / 3600);
        return formatNumberWithUnit(hours, 'hours');
      default:
        return value;
    }
  };

  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy={4}
        textAnchor="middle"
        fill={colors.grey}
        fontFamily={fonts.regular.family}
        fontWeight={fonts.regular.weight}
        fontSize={12}
      >
        {formatYValue(payload.value)}
      </text>
    </g>
  );
};

type CustomizedXAxisLabelProps = {
  x: number;
  y: number;
  payload: any;
  timeFrame: TimeFrameValue;
};

const CustomizedXAxisLabel = ({ x, y, payload, timeFrame }: CustomizedXAxisLabelProps) => {
  const dateFormat: Intl.DateTimeFormatOptions = (() => {
    switch (timeFrame) {
      case 'last-12-weeks':
        return { month: 'short', day: 'numeric' };
      case 'year':
        return { month: 'short' };
      case 'all-time':
        return { month: 'short', year: 'numeric' };
    }
  })();

  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy={4}
        textAnchor="middle"
        fill={colors.grey}
        fontFamily={fonts.regular.family}
        fontWeight={fonts.regular.weight}
        fontSize={12}
      >
        {formatDateTime(
          dayjs(payload.value, PROFILE_BAR_CHART_INTERNAL_DATE_FORMAT).toDate(),
          dateFormat,
        )}
      </text>
    </g>
  );
};
